// Vendor Libraries
import uuid4 from 'uuid-random';
import cloneDeep from 'lodash.clonedeep';
// Constants
import { CUSTOM_TEXT_MODULES, PAGE_SIZE_MAX, PAGE_WIDTH_MAX, PROPOSAL_MODULES, TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR, TRANSITION_ANALYSIS_MODULES, } from '../constants';
export const getPageNum = (moduleOrPageElement) => {
    return parseInt(moduleOrPageElement.dataset.page, 10);
};
export const getPageNumFromPageId = (pageId) => {
    return parseInt(pageId.replace('pdf-page-', '').split('-')[0], 10);
};
export const getCustomizableFieldValues = (fields) => {
    return Object.keys(fields).reduce((acc, fieldName) => {
        acc[fieldName] = fields[fieldName].value;
        return acc;
    }, {});
};
export const convertModulesToSaveState = (modules) => {
    return modules.map((page) => {
        return page.map((module) => {
            const savedModule = {
                ...module,
                // Ignore module field metadata and reduce customizable fields to their values
                customizable_fields: getCustomizableFieldValues(module.customizable_fields),
            };
            if (!PROPOSAL_MODULES.has(module.module_type) &&
                !TRANSITION_ANALYSIS_MODULES.has(module.module_type) &&
                !CUSTOM_TEXT_MODULES.has(module.module_type)) {
                // eslint-disable-next-line no-param-reassign
                delete savedModule.step_uuid;
            }
            return savedModule;
        });
    });
};
/**
 * Checks if the space at the given coordinates, size and width is empty on a given page.
 * @param page The page to check for empty space.
 * @param x The x-coordinate of the space to check.
 * @param y The y-coordinate of the space to check.
 * @param size The size of the module to check.
 * @param width The width of the module to check.
 *
 * @return {boolean} Returns true if the space is empty, false otherwise.
 */
export const isSpaceEmpty = (page, x, y, size, width) => {
    return page.every((module) => {
        if (module.x_coordinate === undefined || module.y_coordinate === undefined)
            return false;
        const xOverlap = x < module.x_coordinate + module.module_width && module.x_coordinate < x + width;
        const yOverlap = y < module.y_coordinate + module.module_size / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR &&
            module.y_coordinate < y + size / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR;
        return !(xOverlap && yOverlap);
    });
};
/**
 * Finds an empty space in a report to place a module of a given size and width, starting at the top of the report.
 * If `selectedEmptyModulePosition` is provided, it'll start searching from that position.
 *
 * @param {number} modules - The report modules.
 * @param {number} size - The size of the module to place.
 * @param {number} width - The width of the module to place.
 * @param {Object|null} selectedEmptyModulePosition - The position of the selected empty module to start searching from.
 *
 * @return {Object|null} Returns the coordinates of the empty space found, or null if no space is available.
 */
export const findEmptySpace = (modules, size, width, selectedEmptyModulePosition = null) => {
    const pageHeight = PAGE_SIZE_MAX / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR;
    const moduleSizeInCoordinateGrid = size / TEARSHEET_PAGE_SIZE_CONVERSION_FACTOR;
    const startPage = selectedEmptyModulePosition?.page ?? 0;
    const startX = selectedEmptyModulePosition?.x ?? 0;
    const startY = selectedEmptyModulePosition?.y ?? 0;
    for (let pageIndex = startPage; pageIndex < modules.length; pageIndex += 1) {
        const page = modules[pageIndex];
        let y = startPage === pageIndex ? startY : 0;
        for (y; y + moduleSizeInCoordinateGrid <= pageHeight; y += 1) {
            let x = startPage === pageIndex && startY === y ? startX : 0;
            for (x; x + width <= PAGE_WIDTH_MAX; x += 1) {
                if (isSpaceEmpty(page, x, y, size, width)) {
                    return { page: pageIndex, x, y };
                }
            }
        }
    }
    return null;
};
export const addModuleToReport = (defaultModule, x, y) => {
    const module = {
        ...defaultModule,
        x_coordinate: x,
        y_coordinate: y,
    };
    // Assign an uuid for tracking purposes if the module doesn't have an ID yet (i.e. it's not saved)
    module.step_uuid = uuid4();
    return module;
};
export const isSameModule = (moduleA, moduleB) => {
    const moduleAId = moduleA.module_id?.toString() ?? moduleA.step_uuid;
    const moduleBId = moduleB.module_id?.toString() ?? moduleB.step_uuid;
    return moduleAId === moduleBId;
};
export const getModulePositionValue = (page, xCoordinate, yCoordinate) => {
    return page * PAGE_SIZE_MAX + yCoordinate + xCoordinate * 0.01;
};
/**
 * Adds modules to a report, starting after the last module in the report (or at the given empty module position).
 *
 * @param modulesToAdd: The modules to add to the report
 * @param modules: All modules in the report
 * @param includeBenchmarkDefault: The default value for the include_benchmark customizable field
 * @param emptyModulePosition: The position of the selected empty module to start searching from
 */
export const addModulesToReport = (modulesToAdd, modules, includeBenchmarkDefault = false, emptyModulePosition = null) => {
    const pagesWithModulesAdded = cloneDeep(modules);
    // Find the last module in the report
    let lastModulePosition = { page: 0, x: 0, y: 0 };
    if (!emptyModulePosition) {
        pagesWithModulesAdded.forEach((page, pageIndex) => {
            page.forEach((module) => {
                const positionValue = getModulePositionValue(pageIndex, module.x_coordinate, module.y_coordinate);
                const emptyModulePositionValue = getModulePositionValue(lastModulePosition.page, lastModulePosition.x, lastModulePosition.y);
                if (positionValue >= emptyModulePositionValue) {
                    lastModulePosition = { page: pageIndex, x: module.x_coordinate, y: module.y_coordinate };
                }
            });
        });
    }
    const modulesAdded = [];
    let selectedModule = null;
    // Add each module to the report, starting after the last module position
    modulesToAdd.forEach((module) => {
        // Get the default module configuration
        const defaultModule = cloneDeep(module.module_menu[module.default_report_type][module.default_page_size]);
        // Find an empty space in the report to place the module, starting after the last module position
        let emptySpace = findEmptySpace(pagesWithModulesAdded, defaultModule.module_size, defaultModule.module_width, emptyModulePosition || lastModulePosition);
        // If we still couldn't find a position, we will add a new page and place the module there
        if (!emptySpace) {
            emptySpace = { page: pagesWithModulesAdded.length, x: 0, y: 0 };
            pagesWithModulesAdded.push([]);
        }
        const moduleToAdd = addModuleToReport(defaultModule, emptySpace.x, emptySpace.y);
        if (moduleToAdd.customizable_fields && 'include_benchmark' in moduleToAdd.customizable_fields) {
            moduleToAdd.customizable_fields.include_benchmark.value = includeBenchmarkDefault;
        }
        pagesWithModulesAdded[emptySpace.page].push(moduleToAdd);
        modulesAdded.push(moduleToAdd);
        if (!selectedModule) {
            selectedModule = {
                module: moduleToAdd,
                page: emptySpace.page,
                index: pagesWithModulesAdded[emptySpace.page].length - 1,
            };
        }
    });
    return { pagesWithModulesAdded, modulesAdded, selectedModule: selectedModule };
};
/**
 * Splits the modules into two arrays: one with all modules before the given position,
 * and one with all modules after and including the given position (including the page number for each module).
 *
 * @param modules: All modules in the report
 * @param position: The position to split the modules at
 */
export const splitModulesAtPosition = (modules, position) => {
    // Sort page that the position is on
    const currentPage = [...modules[position.page]].sort((a, b) => a.y_coordinate - b.y_coordinate || a.x_coordinate - b.x_coordinate);
    // Find the first module on or after the position on the same page
    const index = currentPage.findIndex((module) => {
        const positionValue = getModulePositionValue(position.page, module.x_coordinate, module.y_coordinate);
        const emptyModulePositionValue = getModulePositionValue(position.page, position.x, position.y);
        return positionValue >= emptyModulePositionValue;
    });
    // Get all modules that come before the position
    const beforeModulesOnCurrentPage = index !== -1 ? currentPage.slice(0, index) : currentPage;
    const beforeModules = [...modules.slice(0, position.page), beforeModulesOnCurrentPage];
    // Get all modules that come after the position
    const afterModulesOnCurrentPage = index !== -1 ? currentPage.slice(index) : [];
    const afterModules = [afterModulesOnCurrentPage, ...modules.slice(position.page + 1)];
    const afterModulesWithPages = afterModules.map((page, pageIndex) => {
        return page.map((module) => ({
            ...module,
            page: position.page + pageIndex,
        }));
    });
    return { beforeModules, afterModulesWithPages };
};
/**
 * Repositions the given modules in the report, starting after the last module in the report.
 *
 * @param modules: All modules in the report
 * @param modulesToBeRepositioned: The modules to be repositioned, with their original page numbers
 */
export const repositionModulesInReport = (modules, modulesToBeRepositioned) => {
    // Clone the modules to avoid mutating the original array
    const allModules = cloneDeep(modules);
    // Find the last module in the report to start repositioning after it
    const currentPage = allModules.length ? allModules[allModules.length - 1] : null;
    const lastModuleOnCurrentPage = currentPage ? currentPage[currentPage.length - 1] : null;
    let prevModulePosition = lastModuleOnCurrentPage
        ? {
            page: allModules.length - 1,
            x: lastModuleOnCurrentPage.x_coordinate,
            y: lastModuleOnCurrentPage.y_coordinate,
        }
        : { page: allModules.length - 1, x: 0, y: 0 };
    const modulesToBeRepositionedFlat = modulesToBeRepositioned.flat();
    // Iterate through each module that needs to be repositioned
    modulesToBeRepositionedFlat.forEach((moduleWithPage) => {
        const { page, ...module } = moduleWithPage;
        const moduleToBeUpdated = cloneDeep(module);
        // Find the next available empty space in the report after the previous module
        let emptySpace = findEmptySpace(allModules, moduleToBeUpdated.module_size, moduleToBeUpdated.module_width, prevModulePosition);
        // If no space is found, create a new page and place the module at the top, left corner
        if (!emptySpace) {
            allModules.push([]);
            emptySpace = { page: allModules.length - 1, x: 0, y: 0 };
        }
        // If the next available space is before the module's old position, then keep it in its old position
        const oldPositionValue = getModulePositionValue(page, moduleToBeUpdated.x_coordinate, moduleToBeUpdated.y_coordinate);
        const isOldSpaceEmpty = allModules.length - 1 < page ||
            isSpaceEmpty(allModules[page], moduleToBeUpdated.x_coordinate, moduleToBeUpdated.y_coordinate, moduleToBeUpdated.module_size, moduleToBeUpdated.module_width);
        const newPositionValue = getModulePositionValue(emptySpace.page, emptySpace.x, emptySpace.y);
        if (isOldSpaceEmpty && oldPositionValue > newPositionValue) {
            while (allModules.length - 1 < page) {
                allModules.push([]);
            }
            allModules[page].push(moduleToBeUpdated);
            prevModulePosition = {
                page: allModules.length - 1,
                x: moduleToBeUpdated.x_coordinate,
                y: moduleToBeUpdated.y_coordinate,
            };
        }
        else {
            // If the next available space is after the module's old position, then move it to the new position
            moduleToBeUpdated.x_coordinate = emptySpace.x;
            moduleToBeUpdated.y_coordinate = emptySpace.y;
            allModules[emptySpace.page].push(moduleToBeUpdated);
            prevModulePosition = emptySpace;
        }
    });
    return allModules;
};
